import "./DebugArea.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import UpdateIndicator from "../../custom/UpdateIndicator/UpdateIndicator";
import * as EnvironmentUtils from "../../../lib/util/EnvironmentUtils";
import * as Utils from "../../../lib/util/Utils";
import RestrictedModeIndicator from "../../custom/RestrictedModeIndicator/RestrictedModeIndicator";
import * as UserSelectors from "../../../store/User/User.selector";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import DebugAlerts from "./sub-components/DebugAlerts";
import { useSelector } from "../../../lib/hook/ReduxHooks";
import DebugUserLevelEmulation from "./sub-components/DebugUserLevelEmulation";
import { useFlag } from "../../../lib/hook/MiscHooks";
import * as Flags from "../../../lib/Flags";
import { toTitleCase } from "../../../lib/util/StringUtils";
import Duration from "../Duration/Duration";

const DebugPanel = React.lazy(() => import("./sub-components/DebugPanel"));

let setVisibleForCurrentInstance: any = null;

const DebugArea = () => {
	const { ostrichMode } = useSelector((state) => ({
		ostrichMode: UserSelectors.selectIsCurrentScopeFleetOutsider(state),
	}));

	const [section, setSection] = useState(0);
	const [visible, setVisible] = useState(false);
	// TODO: remove this hack
	setVisibleForCurrentInstance = setVisible;
	const [hotReloaded, setHotReloaded] = useState(false);
	const ref = useRef<HTMLDivElement | null>(null);
	const shown = useRef(false);

	const hideSAdminFeatures = useFlag(Flags.HIDE_SUPER_ADMIN_FEATURES);

	const hotKeyListener = useCallback((e: KeyboardEvent) => {
		if (Utils.isDebugHotkey(e)) {
			e.preventDefault();
			e.stopPropagation();
			setVisible((visible) => {
				const activeElement = document.activeElement;
				if (activeElement && !visible && ref.current?.contains(activeElement)) {
					(activeElement as any).blur();
				}
				return !visible;
			});
		}
	}, []);

	useEffect(() => {
		window.addEventListener("keydown", hotKeyListener);
		return () => window.removeEventListener("keydown", hotKeyListener);
	}, [hotKeyListener]);

	if (visible) {
		shown.current = true;
	}

	const appInfo = EnvironmentUtils.getAppInfo();

	return (
		<div
			className={clsx("DebugArea --pdf-ignore", {
				"--visible": visible,
			})}
			ref={ref}
		>
			<div
				className={clsx("DebugArea__hot-reloaded", hotReloaded && "--visible")}
			>
				<span role={"img"} aria-label={"Hammer"}>
					🔨
				</span>{" "}
				Build completed
			</div>
			{shown.current && (
				<div>
					<React.Suspense
						fallback={
							<LoadingSpinner
								className={"DebugPanel__loading"}
								visible={true}
							/>
						}
					>
						<DebugPanel
							onSectionChange={setSection}
							visible={visible}
							section={section}
							ostrichMode={Boolean(ostrichMode)}
						/>
					</React.Suspense>
				</div>
			)}
			{!hideSAdminFeatures.is() && (
				<>
					<UpdateIndicator />
					<RestrictedModeIndicator />
					<DebugUserLevelEmulation />
					<DebugAlerts />
				</>
			)}
			{!EnvironmentUtils.isProduction() && !visible && (
				<div className={clsx("DebugArea__env-info")}>
					{toTitleCase(EnvironmentUtils.getAppEnvironment(), true)} v
					{appInfo.version}
					{appInfo.ci ? (
						<>
							{" "}
							({appInfo.build!.commit.substring(0, 6)})
							<small>
								<br /> last updated{" "}
								<Duration
									ago={true}
									inline={true}
									from={appInfo.build!.started}
								/>
							</small>
						</>
					) : (
						""
					)}
				</div>
			)}
		</div>
	);
};

export default Object.assign(React.memo(DebugArea), {
	toggle: () => {
		if (setVisibleForCurrentInstance) {
			setVisibleForCurrentInstance((visible: boolean) => !visible);
		}
	},
});
